/**
 * 내용이 같은 갱신은 상태를 바꾸지 않는다 — 트랙 B-1 (docs/DESIGN-perf-track.md §3).
 *
 * 폴링·새로고침이 같은 데이터를 새 참조로 내려놓으면 페이지 전체가 다시 그려진다(실측: /simulations 10초마다
 * 2,154 fiber·0.4~6.4초, 떠날 때 0~4.6초 대기). 주기 갱신의 세터는 여기서 만든 세터를 써서, 구조적으로 같은 값이면
 * 이전 참조를 유지한다(React 는 같은 참조면 재렌더하지 않는다). 상태 갱신 억제의 비교 규칙은 이 파일 한 곳.
 */

/**
 * 내용 동등: 원시값(Object.is — NaN 은 같고 0/-0 은 다름)·배열(순서 포함)·Map·Set(원소는 참조로)·Date·객체(own 키, 프로토타입 무시 —
 * 클래스 인스턴스와 같은 모양의 평범한 객체는 같다고 본다; protobuf-es 메시지의 필드는 own 속성이라 그대로 비교된다).
 * 순환 참조는 다루지 않는다(상태 데이터에 없다). 도메인의 projectDelta diff(JSON 문자열 비교)와는 별개의 규칙이다.
 */
export function sameContent(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (a instanceof Date || b instanceof Date) return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) if (!sameContent(a[i], b[i])) return false;
    return true;
  }
  if (a instanceof Map || b instanceof Map) {
    if (!(a instanceof Map) || !(b instanceof Map) || a.size !== b.size) return false;
    for (const [k, v] of a) if (!b.has(k) || !sameContent(v, b.get(k))) return false;
    return true;
  }
  if (a instanceof Set || b instanceof Set) {
    if (!(a instanceof Set) || !(b instanceof Set) || a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
  }
  const ka = Object.keys(a as Record<string, unknown>);
  const kb = Object.keys(b as Record<string, unknown>);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.prototype.hasOwnProperty.call(b, k)) return false;
    if (!sameContent((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  }
  return true;
}

/** 같으면 이전 참조, 다르면 다음 값 */
export function settle<T>(prev: T, next: T): T {
  return sameContent(prev, next) ? prev : next;
}

type Setter<T> = (update: T | ((prev: T) => T)) => void;

/** React 상태 세터를 감싼다: 값이 구조적으로 같으면 이전 참조를 유지해 재렌더를 막는다 */
export function keepIfSame<T>(set: Setter<T>): (next: T) => void {
  return (next) => set((prev: T) => settle(prev, next));
}
